import InputFilter from 'volta/components/filters/input-filter';
import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { restartableTask } from 'ember-concurrency';
import {
  CollectionViewEvents,
  CollectionViewResourceName,
  columnsToProps
} from 'volta/models/collection-view';
import { normalize } from 'volta/utils/api/serialize-and-push';

import type Owner from '@ember/owner';
import type { IResourceName } from 'volta/models/base-model';
import type CollectionView from 'volta/models/collection-view';
import type {
  ICollectionDefinitionBuilder,
  ICollectionSortOption,
  ICollectionViewDefinition,
  ICreateCollectionViewCmd
} from 'volta/models/types/collection-view';
import type { SingleResourceDoc } from 'volta/utils/api/jsonapi-types';
import type CustomFieldDefinitionsService from './custom-field-definitions-service';
import type EventStreamService from './event-stream';
import type StoreService from './store';
import type { IBvTableColumn } from 'volta/components/bv-table';
import type { IntlService } from 'ember-intl';
import type SessionUserService from './session-user';

/**
 * Base class for collection definition builders
 */
abstract class CollectionBuilderBase extends Service implements ICollectionDefinitionBuilder {
  @service eventStream!: EventStreamService;
  @service customFieldDefinitionsService!: CustomFieldDefinitionsService;
  @service store!: StoreService;
  @service intl!: IntlService;
  @service sessionUser!: SessionUserService;

  /**
   * The user's collection views for this resource type
   */
  @tracked myCollectionViews?: CollectionView[];

  /**
   * The resource name this collection builder is for
   */
  abstract resourceName: IResourceName;

  constructor(owner: Owner) {
    super(owner);

    // Listen for collection view changes

    this.eventStream.addHandler(
      CollectionViewResourceName.plural,
      CollectionViewEvents.CollectionViewCreated,
      this._handleCollectionViewCreated,
      this
    );
    this.eventStream.addHandler(
      CollectionViewResourceName.plural,
      CollectionViewEvents.CollectionViewUpdated,
      this._handleCollectionViewUpdated,
      this
    );
    this.eventStream.addHandler(
      CollectionViewResourceName.plural,
      CollectionViewEvents.CollectionViewDeleted,
      this._handleCollectionViewDeleted,
      this
    );
  }

  /**
   * Build filter definitions for the collection view
   * @param _queryParams  The query params from the route
   * @returns  The filter definitions
   */
  buildFilters(_queryParams: TQueryParams): TFilterDefinition<unknown>[] {
    console.warn('buildFilters not implemented');
    return [
      {
        queryParam: 'q',
        apiParam: 'q',
        comparators: ['lk'],
        component: InputFilter as TBaseFilterComponent,
        hidden: true,
        componentArgs: { inputType: 'text' }
      }
    ];
  }

  /**
   * Build the collection view definition
   * @param queryParams  The query params from the route
   * @param sorts  The sort options for the collection view
   * @returns  The collection view definition
   */
  abstract build(
    queryParams: TQueryParams,
    sorts: ICollectionSortOption[]
  ): ICollectionViewDefinition;

  /**
   * Build the default collection view command
   * @param columns The columns to include in the default view
   * @returns The default collection view command
   */
  buildDefaultView(columns: IBvTableColumn[]): ICreateCollectionViewCmd {
    const { intl, sessionUser } = this;
    const properties = columnsToProps(columns);

    return {
      name: intl.t('collectionViewsPage.default.name', {
        resourceNamePlural: intl.t(this.resourceName.plural)
      }),
      description: intl.t('collectionViewsPage.default.description', {
        resourceNamePlural: intl.t(this.resourceName.plural)
      }),
      collectionType: 'LIST',
      entityType: this.resourceName.plural,
      properties,
      createdBy: sessionUser.userId,
      sortBy: [{ property: 'createdAt', asc: false }],
      pageSize: 100
    };
  }

  /**
   * Fetch the user's collection views for this resource type
   */
  fetchCollectionViews = restartableTask(async () => {
    if (!this.myCollectionViews) {
      const data = await this.store.rawQuery('collection-view', ['mine', this.resourceName.plural]);
      this.myCollectionViews = normalize('collection-view', data) as CollectionView[];
    }
  });

  /**
   * Handle collection view created event
   * @param payload  The event payload
   */
  private _handleCollectionViewCreated(payload: SingleResourceDoc) {
    if (
      this.myCollectionViews &&
      payload.data.attributes?.['entityType'] === this.resourceName.plural
    ) {
      const collectionView = normalize('collection-view', payload) as CollectionView;

      this.myCollectionViews = [...new Set([...(this.myCollectionViews ?? []), collectionView])];
    }
  }

  /**
   * Handle collection view updated event
   * @param payload  The event payload
   */
  private _handleCollectionViewUpdated(payload: SingleResourceDoc) {
    if (
      this.myCollectionViews &&
      payload.data.attributes?.['entityType'] === this.resourceName.plural
    ) {
      const collectionView = normalize('collection-view', payload) as CollectionView;

      this.myCollectionViews = (this.myCollectionViews ?? []).map((view) => {
        if (collectionView.id === view.id) {
          return collectionView;
        }
        return view;
      });
    }
  }

  /**
   * Handle collection view deleted event
   * @param payload  The event payload
   */
  private _handleCollectionViewDeleted(payload: SingleResourceDoc) {
    if (
      this.myCollectionViews &&
      payload.data.attributes?.['entityType'] === this.resourceName.plural
    ) {
      const collectionView = normalize('collection-view', payload) as CollectionView;

      this.myCollectionViews = (this.myCollectionViews ?? []).filter(
        (view) => collectionView.id !== view.id
      );
    }
  }
}

export default CollectionBuilderBase;
